/**
 * CanvasApplication - Main application class for CanvasApp_v2.
 * Extends BaseApplication to provide canvas-specific Y.js, AI, and collaboration functionality.
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { CANVAS_APP_CONFIG, getClassesByCategory } from './CanvasConfig';
import { entityService } from '../../../services/entityService';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { generateUserColor } from '../../shared/appHelpers';

export class CanvasApplication extends BaseApplication {
  public undoManager: Y.UndoManager | null = null;

  constructor() {
    super();
    this.suggestionMode = false;
  }

  protected getConfig(): AppConfig {
    return CANVAS_APP_CONFIG;
  }

  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    try {
      const entity = await entityService.get(entityId);

      if (!entity) {
        throw new Error(`Canvas not found: ${entityId}`);
      }

      this.entityId = entityId;
      this.ydoc = new Y.Doc();

      if (entity.skeleton?.base64) {
        try {
          this.loadBase64State(entity.skeleton.base64);
        } catch (error) {
          console.error('[CanvasApplication] Failed to load Y.js state:', error);
          this.emit('loadError', {
            error,
            message: 'Failed to load canvas data. Starting with empty canvas.',
          });
        }
      }

      this.yxml = this.ydoc.getXmlFragment('prosemirror');

      this.undoManager = new Y.UndoManager([this.yxml], {
        trackedOrigins: new Set([this.ydoc.clientID]),
        captureTimeout: 300,
      });

      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyCanvas();
      }

      this.setupAutoSave();

      await this.buildCoreIndices();

      if (!options.offline) {
        await this.setupCollaborationWithDoc(entityId, this.ydoc);
      }
    } catch (error) {
      console.error('[CanvasApplication] Failed to load:', error);
      throw error;
    }
  }

  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();

    if (!authUser) {
      return;
    }

    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id),
    };

    this.collaborationProvider = createCollaborationProvider({
      documentId: entityId,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });

    this.collaborationProvider.connect();

    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;

      const cleanup = () => {
        if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
          syncedHandler = null;
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
          errorHandler = null;
        }
      };

      timeoutId = setTimeout(() => {
        cleanup();
        // Destroy (not just disconnect) when the server is unreachable.
        // WebsocketProvider.disconnect() keeps retrying; destroy() stops it permanently.
        try {
          this.collaborationProvider?.destroy();
          this.collaborationProvider = undefined;
        } catch (_) {}
        resolve();
      }, 5000);

      syncedHandler = () => { cleanup(); resolve(); };
      errorHandler = (error: Error) => {
        console.error('[CanvasApplication] Sync error:', error);
        cleanup();
        resolve();
      };

      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
  }

  protected getWebSocketUrl(): string {
    if (typeof process !== 'undefined' && process.env.REACT_APP_YWEBSOCKET_URL) {
      return process.env.REACT_APP_YWEBSOCKET_URL;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    return `${protocol}//${host}:1234`;
  }

  private initializeEmptyCanvas(): void {
    // Canvas starts empty — no default elements.
  }

  public getElementsByCategory(category: string): SerializedNode[] {
    const classNames = getClassesByCategory(category);
    const elements: SerializedNode[] = [];

    for (const className of classNames) {
      const nodeIds = this.byClassIndex.get(className);
      if (nodeIds) {
        for (const nodeId of nodeIds) {
          const node = this.byIdIndex.get(nodeId);
          if (node) elements.push(node as any);
        }
      }
    }

    return elements;
  }

  public getAllShapes(): SerializedNode[] {
    return this.getElementsByCategory('shape');
  }

  public getAllStickyNotes(): SerializedNode[] {
    const nodeIds = this.byClassIndex.get('sticky-note');
    if (!nodeIds) return [];
    return Array.from(nodeIds)
      .map(id => this.byIdIndex.get(id))
      .filter((node): node is any => node !== undefined);
  }

  public getAllTextElements(): SerializedNode[] {
    return this.getElementsByCategory('text');
  }

  public getAllConnectors(): SerializedNode[] {
    return this.getElementsByCategory('connector');
  }

  public getElementsInRegion(bounds: { x: number; y: number; width: number; height: number }): SerializedNode[] {
    const results: SerializedNode[] = [];

    if (this.spatialIndex) {
      const spatialNodes = this.spatialIndex.query({ type: 'bounds' as const, bounds });
      return spatialNodes.map(sn => this.byIdIndex.get(sn.id)!).filter(Boolean) as any;
    }

    for (const node of this.byIdIndex.values()) {
      const pos = node.attrs?.position;
      if (pos && pos.type === 'absolute') {
        const intersects = (
          pos.x < bounds.x + bounds.width &&
          pos.x + (pos.width || 0) > bounds.x &&
          pos.y < bounds.y + bounds.height &&
          pos.y + (pos.height || 0) > bounds.y
        );
        if (intersects) results.push(node as any);
      }
    }

    return results;
  }

  public getCanvasStats() {
    const allNodes = Array.from(this.byIdIndex.values());
    return {
      totalElements: allNodes.length,
      shapes: this.getAllShapes().length,
      stickyNotes: this.getAllStickyNotes().length,
      textElements: this.getAllTextElements().length,
      connectors: this.getAllConnectors().length,
      images: this.getNodesByClassName('canvas-image').length,
      groups: this.getNodesByClassName('canvas-group').length,
    };
  }

  public getCanvasBounds(): { x: number; y: number; width: number; height: number } | null {
    const allNodes = Array.from(this.byIdIndex.values());
    if (allNodes.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const node of allNodes) {
      const pos = node.attrs?.position;
      if (pos && pos.type === 'absolute') {
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + (pos.width || 0));
        maxY = Math.max(maxY, pos.y + (pos.height || 0));
      }
    }

    if (minX === Infinity) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  public exportToSVG(): string {
    const bounds = this.getCanvasBounds();
    if (!bounds) return '<svg></svg>';

    let svg = `<svg width="${bounds.width}" height="${bounds.height}" xmlns="http://www.w3.org/2000/svg">`;

    const allNodes = Array.from(this.byIdIndex.values());
    for (const node of allNodes) {
      const className = node.attrs?.className;
      const pos = node.attrs?.position;
      const style = node.attrs?.style;

      if (!pos || pos.type !== 'absolute') continue;

      const svgX = pos.x - bounds.x;
      const svgY = pos.y - bounds.y;

      if (className?.startsWith('shape-rectangle')) {
        svg += `<rect x="${svgX}" y="${svgY}" width="${pos.width}" height="${pos.height}" fill="${style?.fill || '#ccc'}" stroke="${style?.stroke || '#000'}" />`;
      } else if (className?.startsWith('shape-circle')) {
        const r = (pos.width || 0) / 2;
        svg += `<circle cx="${svgX + r}" cy="${svgY + r}" r="${r}" fill="${style?.fill || '#ccc'}" stroke="${style?.stroke || '#000'}" />`;
      }
    }

    svg += '</svg>';
    return svg;
  }

  public searchElements(query: string): SerializedNode[] {
    const lowerQuery = query.toLowerCase();
    const results: SerializedNode[] = [];

    for (const node of this.byIdIndex.values()) {
      const text = this.extractTextFromNode(node as any);
      if (text.toLowerCase().includes(lowerQuery)) {
        results.push(node as any);
      }
    }

    return results;
  }

  private extractTextFromNode(node: SerializedNode): string {
    if (node.text) return node.text;
    if (node.content) {
      return node.content.map(child => this.extractTextFromNode(child)).join(' ');
    }
    return '';
  }

  async handleEntityRead(selector: any, options: any = {}): Promise<any> {
    if (selector.bounds) {
      const elements = this.getElementsInRegion(selector.bounds);
      return {
        nodes: elements,
        wordCount: this.calculateWordCount(elements),
        stats: { nodeCount: elements.length, queryType: 'spatial-bounds' },
      };
    }
    return super.handleEntityRead(selector, options);
  }

  async handleEntityWrite(params: {
    operation: 'insert' | 'update' | 'delete' | 'batch';
    content?: any;
    attrs?: any;
    target?: any;
    nodeId?: string;
    operations?: any[];
  }): Promise<any> {
    if (!this.yxml || !this.ydoc) {
      throw new Error('Canvas not initialized');
    }

    if (params.operation === 'batch' && params.operations) {
      return this.handleBatchWrite(params);
    }

    if (params.operation === 'update') {
      return this.handleCanvasUpdate(params);
    }

    if (params.content?.attrs?.text) {
      params.content.attrs.text = this.stripMarkdownDelimiters(params.content.attrs.text);
    }

    if (params.attrs?.text) {
      params.attrs.text = this.stripMarkdownDelimiters(params.attrs.text);
    }

    return super.handleEntityWrite(params);
  }

  private async handleCanvasUpdate(params: any): Promise<any> {
    const target = params.target || {};
    const attrs = params.attrs || {};

    await this.buildCoreIndices();

    const nodeIds = this.findNodesByTargetCanvas(target);

    if (nodeIds.length === 0) {
      return {
        success: false,
        error: 'No elements found matching target selector',
        operation: 'update',
        stats: { nodesModified: 0 },
      };
    }

    const affectedIds: string[] = [];
    const errors: any[] = [];

    this.ydoc!.transact(() => {
      for (const nodeId of nodeIds) {
        try {
          const index = this.findNodeIndexByIdCanvas(nodeId);
          if (index >= 0 && this.yxml) {
            const element = this.yxml.get(index) as Y.XmlElement;
            this.updateCanvasElement(element, attrs);
            affectedIds.push(nodeId);
          }
        } catch (error: any) {
          console.error('[CanvasApplication] Failed to update element:', nodeId, error);
          errors.push({ nodeId, error: error.message });
        }
      }
    });

    await this.buildCoreIndices();
    await this.saveToBackend();

    return {
      success: affectedIds.length > 0,
      operation: 'update',
      stats: { nodesModified: affectedIds.length, nodesFailed: errors.length },
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  private async handleBatchWrite(params: any): Promise<any> {
    const operations = params.operations || [];

    if (operations.length === 0) {
      return {
        success: false,
        error: 'No operations provided in batch',
        operation: 'batch',
        stats: { total: 0, succeeded: 0, failed: 0 },
      };
    }

    await this.buildCoreIndices();

    const results: Array<{ success: boolean; operation?: string; nodesModified?: number; error?: string }> = [];
    let totalSucceeded = 0;
    let totalFailed = 0;

    this.ydoc!.transact(() => {
      for (let i = 0; i < operations.length; i++) {
        const op = operations[i];
        const operation = op.op || op.operation;
        const target = op.target;
        const content = op.content;
        const attrs = op.attrs;

        try {
          if (operation === 'update') {
            const nodeIds = this.findNodesByTargetCanvas(target);
            const affectedIds: string[] = [];
            for (const nodeId of nodeIds) {
              const index = this.findNodeIndexByIdCanvas(nodeId);
              if (index >= 0 && this.yxml) {
                const element = this.yxml.get(index) as Y.XmlElement;
                this.updateCanvasElement(element, attrs);
                affectedIds.push(nodeId);
              }
            }
            results.push({ success: affectedIds.length > 0, operation: 'update', nodesModified: affectedIds.length });
            affectedIds.length > 0 ? totalSucceeded++ : totalFailed++;
          } else if (operation === 'insert') {
            if (!content) {
              results.push({ success: false, operation: 'insert', error: 'Missing content for insert operation' });
              totalFailed++;
              continue;
            }
            const affectedIds = this.executeInsert({ content, target, operation: 'insert' });
            results.push({ success: affectedIds.length > 0, operation: 'insert', nodesModified: affectedIds.length });
            affectedIds.length > 0 ? totalSucceeded++ : totalFailed++;
          } else if (operation === 'delete' || operation === 'remove') {
            if (!target) {
              results.push({ success: false, operation: 'delete', error: 'Missing target for delete operation' });
              totalFailed++;
              continue;
            }
            const affectedIds = this.executeDelete({ target, operation: 'delete' }, true);
            results.push({ success: affectedIds.length > 0, operation: 'delete', nodesModified: affectedIds.length });
            affectedIds.length > 0 ? totalSucceeded++ : totalFailed++;
          } else if (operation === 'replace') {
            if (!content) {
              results.push({ success: false, operation: 'replace', error: 'Missing content for replace operation' });
              totalFailed++;
              continue;
            }
            const affectedIds = this.executeReplace({ content, target, operation: 'replace' });
            results.push({ success: affectedIds.length > 0, operation: 'replace', nodesModified: affectedIds.length });
            affectedIds.length > 0 ? totalSucceeded++ : totalFailed++;
          } else {
            results.push({ success: false, error: `Unknown batch operation type '${operation}'` });
            totalFailed++;
          }
        } catch (error: any) {
          console.error('[CanvasApplication] Batch op', i + 1, 'failed:', error);
          results.push({ success: false, error: error.message });
          totalFailed++;
        }
      }
    }, 'canvas-batch-write');

    await this.buildCoreIndices();
    await this.saveToBackend();

    return {
      success: totalSucceeded > 0,
      operation: 'batch',
      stats: { total: operations.length, succeeded: totalSucceeded, failed: totalFailed },
      results,
    };
  }

  private updateCanvasElement(element: Y.XmlElement, attrs: any): void {
    const dataAttr = element.getAttribute('data');

    if (!dataAttr) {
      console.error('[CanvasApplication] Element missing data attribute');
      throw new Error('Cannot update element: missing data attribute (element may be in old format)');
    }

    let currentData: any;
    try {
      currentData = JSON.parse(dataAttr);
    } catch (e) {
      console.error('[CanvasApplication] Failed to parse data attribute:', e);
      throw new Error('Cannot update element: corrupt data attribute');
    }

    const updatedData = this.mergeCanvasAttributes(currentData, attrs);

    if (!this.validateCanvasData(updatedData)) {
      console.error('[CanvasApplication] Update would create invalid element:', updatedData);
      throw new Error('Update validation failed: merged data is missing required fields');
    }

    element.setAttribute('data', JSON.stringify(updatedData));
  }

  private mergeCanvasAttributes(currentData: any, attrs: any): any {
    const merged = { ...currentData };

    if (attrs.position) {
      const pos = attrs.position;
      if (pos.x !== undefined) merged.x = pos.x;
      if (pos.y !== undefined) merged.y = pos.y;
      if (pos.width !== undefined) merged.width = pos.width;
      if (pos.height !== undefined) merged.height = pos.height;
    }

    if (attrs.style) {
      merged.style = { ...(merged.style || {}), ...attrs.style };
    }

    if (attrs.text !== undefined) {
      merged.text = this.stripMarkdownDelimiters(attrs.text);
    }

    if (attrs.semantic) {
      merged.semantic = { ...(merged.semantic || {}), ...attrs.semantic };
    }

    if (attrs.start) merged.start = attrs.start;
    if (attrs.end) merged.end = attrs.end;

    Object.keys(attrs).forEach(key => {
      if (!['position', 'style', 'text', 'semantic', 'start', 'end'].includes(key)) {
        merged[key] = attrs[key];
      }
    });

    return merged;
  }

  private validateCanvasData(data: any): boolean {
    if (!data.id || !data.type) {
      console.error('[CanvasApplication] Validation failed: missing id or type');
      return false;
    }

    const isLineOrArrow = data.type === 'line' || data.type === 'arrow';

    if (isLineOrArrow) {
      if (!data.start || !data.end) {
        console.error('[CanvasApplication] Validation failed: line/arrow missing start or end');
        return false;
      }
      if (typeof data.start.x !== 'number' || typeof data.start.y !== 'number') {
        console.error('[CanvasApplication] Validation failed: invalid start point', data.start);
        return false;
      }
      if (typeof data.end.x !== 'number' || typeof data.end.y !== 'number') {
        console.error('[CanvasApplication] Validation failed: invalid end point', data.end);
        return false;
      }
      if (isNaN(data.start.x) || isNaN(data.start.y) || !isFinite(data.start.x) || !isFinite(data.start.y)) {
        console.error('[CanvasApplication] Validation failed: start point is NaN or Infinity');
        return false;
      }
      if (isNaN(data.end.x) || isNaN(data.end.y) || !isFinite(data.end.x) || !isFinite(data.end.y)) {
        console.error('[CanvasApplication] Validation failed: end point is NaN or Infinity');
        return false;
      }
    } else {
      if (typeof data.x !== 'number' || typeof data.y !== 'number') {
        console.error('[CanvasApplication] Validation failed: missing or invalid x/y', { x: data.x, y: data.y });
        return false;
      }
      if (typeof data.width !== 'number' || typeof data.height !== 'number') {
        console.error('[CanvasApplication] Validation failed: missing or invalid width/height', { width: data.width, height: data.height });
        return false;
      }
      if (data.width <= 0 || data.height <= 0) {
        console.error('[CanvasApplication] Validation failed: width/height must be positive', { width: data.width, height: data.height });
        return false;
      }
      if (isNaN(data.x) || isNaN(data.y) || !isFinite(data.x) || !isFinite(data.y)) {
        console.error('[CanvasApplication] Validation failed: position is NaN or Infinity');
        return false;
      }
      if (isNaN(data.width) || isNaN(data.height) || !isFinite(data.width) || !isFinite(data.height)) {
        console.error('[CanvasApplication] Validation failed: size is NaN or Infinity');
        return false;
      }
    }

    return true;
  }

  protected findNodesByTargetCanvas(target: any): string[] {
    if (target.nodeId) return [target.nodeId];
    if (target.nodeIds) return target.nodeIds;
    if (target.className) {
      const nodeIds = this.byClassIndex.get(target.className);
      return nodeIds ? Array.from(nodeIds) : [];
    }
    if (target.bounds) {
      const elements = this.getElementsInRegion(target.bounds);
      return elements.map(el => el.id).filter(Boolean);
    }
    if (target.all === true) {
      return Array.from(this.indexManager.getAllIds());
    }
    return [];
  }

  protected findNodeIndexByIdCanvas(nodeId: string): number {
    if (!this.yxml) return -1;
    for (let i = 0; i < this.yxml.length; i++) {
      const child = this.yxml.get(i);
      if (child instanceof Y.XmlElement) {
        const id = child.getAttribute('id');
        if (id === nodeId) return i;
        try {
          const data = child.getAttribute('data');
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.id === nodeId) return i;
          }
        } catch { /* skip unparseable */ }
      }
    }
    return -1;
  }

  private stripMarkdownDelimiters(text: string): string {
    if (!text) return text;
    return text
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^[-*]\s+/gm, '');
  }

  public override async destroy(): Promise<void> {
    if (this.undoManager) {
      try {
        this.undoManager.destroy();
        this.undoManager = null;
      } catch (error) {
        console.error('[CanvasApplication] Error destroying UndoManager:', error);
      }
    }
    await super.destroy();
  }
}
